import type {InternalOperation, GraphQlResponseError} from '@sail/data';

import SharedRestLink from '../../../shared/graphql/links/RestLink/RestLink';
import type {
  ErrorHandlerStrategy,
  ResponseErrorsParams,
  ErrorCallbackParams,
  MapUnionTypenamesErrorParams,
} from '../../../shared/graphql/links/RestLink/RestLink';
import type {InteropFields} from '../../../shared/graphql/links/RestLink/rewriteInteropParams';
import type {
  UnionObjectTypesMap,
  FieldVersionOverrides,
} from '../../../shared/graphql/links/RestLink/types';
import type {TopLevelFieldToArgs} from '../../../shared/graphql/links/RestLink/removePbeFromParams';
import rebuildErrorFromResponse from './rebuildErrorFromResponse';
import GraphQlGatewayCompatibilityError from '../../../shared/graphql/GraphQlGatewayCompatibilityError';

function reportError(
  prefix: string,
  originalError: unknown,
  operation: InternalOperation,
) {
  const operationName = operation.operationName;
  const context = operation.getContext();
  const extraErrorInfo = {
    operationName,
    service: context.service,
    project: context.queryProject,
    component: context.component,
  };
  const {errors} = context.observabilityFns || {};

  const originalErrorMessage =
    originalError instanceof Error
      ? originalError.message
      : String(originalError);
  const errorMessage = `[${operationName}] ${prefix}: ${originalErrorMessage}`;

  const errorStack =
    originalError instanceof Error ? originalError.stack : undefined;

  errors?.warning(errorMessage, {
    project: 'sail_core',
    extras: {
      error: originalError,
      errorStack,
      ...extraErrorInfo,
    },
  });
}

function createNetworkGraphQlError(): GraphQlResponseError {
  return {
    message: 'Native fetch error: Failed to fetch',
    extensions: {
      error: {
        code: 'network_error',
        source: 'network',
        status: -1,
      },
      unstable_requestId: null,
    },
  };
}

const ERROR_HANDLER: ErrorHandlerStrategy<
  GraphQlResponseError,
  InternalOperation
> = {
  handleResponseErrors: ({
    response,
    observer,
  }: ResponseErrorsParams<GraphQlResponseError>) => {
    observer.next({
      data: null,
      errors: response.errors,
    });
  },
  handleMapUnionTypenamesError: ({
    error,
    observer,
    operation,
  }: MapUnionTypenamesErrorParams<GraphQlResponseError, InternalOperation>) => {
    reportError('Runtime error in mapUnionTypenames', error, operation);

    observer.next({
      data: null,
      errors: [createNetworkGraphQlError()],
    });
  },

  handleErrorCallback: ({
    error,
    observer,
    operation,
  }: ErrorCallbackParams<GraphQlResponseError, InternalOperation>) => {
    if (error instanceof GraphQlGatewayCompatibilityError) {
      const graphQlError: GraphQlResponseError = {
        message: error.message,
        path: error.path,
        extensions: {
          error: {
            code: 'graphql_gateway_compatibility_error',
            status: -1,
            source: 'client',
          },
        },
      };

      observer.next({
        data: null,
        errors: [graphQlError],
      });

      observer.complete();

      return;
    }

    reportError('Error thrown in underlying RestLink', error, operation);

    observer.next({
      data: null,
      errors: [createNetworkGraphQlError()],
    });
    observer.complete();
  },
};

export default class RestLink extends SharedRestLink<
  GraphQlResponseError,
  InternalOperation
> {
  constructor(
    unionObjectTypesMap: UnionObjectTypesMap,
    pbeMap: Record<string, ReadonlyArray<string>>,
    topLevelFieldToArgs: TopLevelFieldToArgs,
    fieldVersionOverrides: FieldVersionOverrides,
    queryEndpointsToBeRequestedWithPost?: string[],
    interopFields?: InteropFields,
  ) {
    super(
      unionObjectTypesMap,
      pbeMap,
      topLevelFieldToArgs,
      fieldVersionOverrides,
      ERROR_HANDLER,
      rebuildErrorFromResponse,
      queryEndpointsToBeRequestedWithPost,
      interopFields,
    );
  }

  request(
    operation: InternalOperation,
    forward?: (arg1: InternalOperation) => any,
  ) {
    return super.request(operation, forward);
  }
}
